import { Log } from '@artup/utilities'
import { EntityType } from '@artup/model'
import { PushClient } from './push-client'

/**
 * Push notification client for Accounts API
 */
export class AccountsPushClient extends PushClient {
  constructor () {
    super()
    this.name = 'accounts'
  }

  /**
   * Push services configuration
   * @type {Object}
   */
  configuration

  /**
   * URL of the subscribe endpoint
   * @type {String}
   */
  get subscribeEndpoint () {
    const endpoint = (this.configuration?.endpoints?.accounts || '').replace(/\/+$/, '')
    if (endpoint.endsWith('/push/subscribe')) {
      return endpoint
    }
    return `${endpoint}/push/subscribe`
  }

  /**
   * URL of the echo endpoint, used to verify whether websockets are supported
   * @type {String}
   */
  get echoEndpoint () {
    const endpoint = (this.configuration?.endpoints?.accounts || '').replace(/\/+$/, '')
    if (endpoint.endsWith('/push/echo')) {
      return endpoint
    }
    if (endpoint.endsWith('/push/subscribe')) {
      return endpoint.replace(/\/push\/subscribe$/, '/push/echo')
    }
    return `${endpoint}/push/echo`
  }

  /**
   * Initializes the push client
   * @param {Object} configuration Push services configuration
   * @returns {Promise<void>}
   */
  async initialize (configuration) {
    if (!configuration) throw new Error('Push services configuration is required')
    if (!configuration.endpoints) throw new Error('Push services endpoint configuration is required')
    if (!configuration.endpoints.accounts) throw new Error('Accounts Push Server endpoint is required')
    this.configuration = configuration

    if (super.initialize) {
      await super.initialize(configuration)
    }
  }

  /**
   * Checks whether the push client has been initialized
   * @returns {Boolean}
   */
  get isInitialized () {
    return Boolean(this.configuration)
  }

  /**
   * Subscribes to account notifications (e.g. logout events)
   * @param {Object} options
   * @param {Account|Object} options.account Logged in account subscribing to notifications
   * @param {String} [options.applet] Calling applet identifier
   * @param {Function} options.onMessage Handler called when notification arrives
   * @param {Function} [options.onError] Error handler
   * @param {Number} [options.reconnectAttempts]
   * @param {Number} [options.reconnectInterval]
   * @returns {Promise<AccountsPushClient>}
   */
  async subscribeToAccountNotifications ({
    account,
    applet,
    onMessage,
    onError,
    reconnectAttempts = PushClient.RECONNECT_INFINITE,
    reconnectInterval = PushClient.RECONNECT_INTERVAL
  } = {}) {
    if (!this.isInitialized) throw new Error('Accounts Push Client is not initialized')
    if (!onMessage) throw new Error('Message handler is required')
    if (!account) throw new Error('Account is required')

    const accountIdentifier = account.name || account.id?.toString()

    if (this.isOpen && this.subscribedAccountIdentifier === accountIdentifier && this.subscribedApplet === (applet || null)) {
      return this
    }

    let url = this.subscribeEndpoint
    if (applet) {
      const separator = url.includes('?') ? '&' : '?'
      url = `${url}${separator}applet=${encodeURIComponent(applet)}`
    }
    const delay = 500
    const message = {
      applet,
      items: [{
        dataType: EntityType.Account || 'account',
        entityType: EntityType.Account || 'account',
        entityId: accountIdentifier
      }]
    }

    const subscribed = await this.subscribe({
      url,
      message,
      delay,
      onMessage,
      onError,
      authenticate: true,
      reconnectAttempts,
      reconnectInterval
    })

    if (subscribed) {
      this.subscribedAccountIdentifier = accountIdentifier
      this.subscribedApplet = applet || null
      Log.debug(`[${url}] Subscribed to account notifications for [${accountIdentifier}]${applet ? ` (${applet})` : ''}`)
      return this
    }
  }

  /**
   * Closes the connection and resets subscription state
   */
  async close (allowReconnect) {
    this.subscribedAccountIdentifier = null
    this.subscribedApplet = null
    return super.close(allowReconnect)
  }
}
